import {UntypedFormBuilder} from '@angular/forms';
import {Component} from '@angular/core';
import {BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap, take, tap} from 'rxjs';
import {ConfigSection} from '../config-section';
import {OverviewConfiguration} from '@admin/models/system-configuration-model';
import {OverviewService} from '@feature/overview/services/overview.service';
import {MojoWellKnownNames} from '@common/constants/mojo-well-known-names';
import {IOverviewTileModel} from '@feature/overview/models/overview-tile-model';
import {SelectItem} from 'primeng/api';
import {OverviewHttpService} from '@feature/overview/services/overview-http.service';
import {ToastrService} from 'ngx-toastr';
import {UserPreferencesHttpService} from "@feature/user-preferences/services/user-preferences-http.service";
import {UserPreferencesService} from '@feature/user-preferences/services/user-preferences.service';
import {PmaEntityLookupHttpService} from '@feature/pma-entity/services/pma-entity-lookup-http.service';

@Component({
  selector: 'mojo-mojo-overview-configuration',
  templateUrl: './overview-configuration.component.html'
})
export class OverviewConfigurationComponent extends ConfigSection<OverviewConfiguration> {
  selectedFieldSubject = new BehaviorSubject(undefined);
  selectedFieldObservable = this.selectedFieldSubject.asObservable();

  readonly productName: string = MojoWellKnownNames.Products.MojoDashboard;
  allTiles: IOverviewTileModel[] = [];
  selectableTiles: SelectItem[] = [];
  filterItems: Observable<SelectItem[]>;
  selectableFields: SelectItem[] = [
    {value: null, label: 'No exclusions'},
    {value: 'CreditorGroup', label: 'Creditor Group'},
    {value: 'CreditorCategory', label: 'Creditor Category'},
    {value: 'PaymentSelServiceType', label: 'Payment Sel Service Type'},
    {value: 'SupplierServiceType', label: 'Supplier Service Type'},
  ];

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly overviewService: OverviewService,
    private readonly overviewHttpService: OverviewHttpService,
    private readonly userPreferencesHttpService: UserPreferencesHttpService,
    private readonly userPreferencesService: UserPreferencesService,
    private readonly toastrService: ToastrService,
    private readonly pmaEntityLookupHttpService: PmaEntityLookupHttpService
  ) {
    super();
  }

  initialiseConfig(): Observable<OverviewConfiguration> {
    this.filterItems = this.selectedFieldObservable.pipe(switchMap(x => x
      ? this.pmaEntityLookupHttpService.lookup(x, '')
      : of([])), map(items => items.map(x => {
      return {label: x.Name, value: x.Key?.Uri}
    })));
    const getOverviewTiles = this.overviewService.getOverviewTilesByProductName(this.productName)
      .pipe(
        take(1),
        catchError(e => {
          this.toastrService.error(`Error getting Overview Tiles by Product Name ${e.status} ${e.message}`)
          return of(null)
        }))
    const getSystemDefaults = this.userPreferencesHttpService.getUserPreferencesSystemDefaults()
      .pipe(
        take(1),
        catchError(e => {
          this.toastrService.error(`Error getting User Preferences System Defaults ${e.status} ${e.message}`)
          return of(null)
        }))
    return forkJoin([getOverviewTiles, getSystemDefaults])
      .pipe(
        map(([overview, systemDefaults]) => {
          this.allTiles = overview.AllTiles;
          this.selectableTiles = this.mapToSelectItemModel(this.allTiles);
          const config: OverviewConfiguration = {
            selectedTiles: this.selectableTiles.filter(t =>
              overview.AvailableTiles
                .map(t2 => t2.Name)
                .includes(t.value))
              .map(t => t.value),
            displayNonCurrentManagers: systemDefaults.DisplayNonCurrentManagers,
            skipListerPage: systemDefaults.SkipListerPage,
            displayObsoleteUnits: systemDefaults.DisplayObsoleteUnits,
            selectedFilterField: overview.SelectedFilterField,
            selectedFilterFieldItems: overview.SelectedFilterFieldItems,
            displayNonCurrentLeasesAndTenants: systemDefaults.DisplayNonCurrentLeasesAndTenants,
            displayNonCurrentHeadLeasesAndLandlords: systemDefaults.DisplayNonCurrentHeadLeasesAndLandlords
          };
          return config
        }),
        tap(x => this.selectedFieldChanged(x.selectedFilterField))
      )
  }

  selectedFieldChanged(selectedField: string) {
    this.form?.controls['selectedFilterItems']?.setValue([]);
    if (selectedField == 'PaymentSelServiceType' || selectedField == 'SupplierServiceType') {
      this.selectedFieldSubject.next('ServiceType');
    } else {
      this.selectedFieldSubject.next(selectedField);
    }
  }

  createForm(config: OverviewConfiguration): void {
    this.form = this.formBuilder.group({
      selectedTiles: [config.selectedTiles],
      displayNonCurrentManagers: [config.displayNonCurrentManagers],
      skipListerPage: [config.skipListerPage],
      displayObsoleteUnits: [config.displayObsoleteUnits],
      filterField: [config.selectedFilterField],
      selectedFilterItems: [config.selectedFilterFieldItems],
      displayNonCurrentLeasesAndTenants: [config.displayNonCurrentLeasesAndTenants],
      displayNonCurrentHeadLeasesAndLandlords: [config.displayNonCurrentHeadLeasesAndLandlords]
    });
  }

  submit(): Observable<boolean> {
    const config = this.form.getRawValue();
    const selectedTiles: IOverviewTileModel[] = this.allTiles.filter(t => config.selectedTiles.includes(t.Name));
    return this.overviewHttpService
      .updateOverviewConfiguration({
        AvailableTiles: selectedTiles,
        Product: this.productName,
        DisplayNonCurrentManagers: config.displayNonCurrentManagers,
        SkipListerPage: config.skipListerPage,
        DisplayObsoleteUnits: config.displayObsoleteUnits,
        SelectedFilterField: config.filterField,
        SelectedFilterFieldItems: config.selectedFilterItems,
        DisplayNonCurrentLeasesAndTenants: config.displayNonCurrentLeasesAndTenants,
        DisplayNonCurrentHeadLeasesAndLandlords: config.displayNonCurrentHeadLeasesAndLandlords
      })
      .pipe(
        tap(_ => {
          // effective values may have changed where user pref is 'use system default'
          this.userPreferencesService.loadUserPreferences();
          // in case the selected tiles have changed
          this.overviewService.loadOverviewConfiguration();
        }),
        map(() => true)
      );
  }

  mapToSelectItemModel(items: IOverviewTileModel[]): SelectItem[] {
    const results: SelectItem[] = [];
    items.forEach(t2 => results.push({label: t2.Label, value: t2.Name}));
    return results;
  }
}
